import { AnalyticsActionSubject, AnalyticsAction } from '../../types';
import { type FailedFetchAttributes } from '../../analytics';
import { BaseInvitePeopleAnalytics } from '../../analytics';
import { FLOW_CORE_INVITES } from '../InvitePeople/InvitePeopleContext';
import { type GetDirectAccessSettingRequest } from './types';

interface HisiFlagAttributes {
	flagKey: string;
	product: string;
	domain?: string;
	value?: boolean;
}

export const SUBJECT_IDS = {
	DIRECT_ACCESS_CHECKBOX: 'directAccessCheckbox',
	USER_INVITES_CHECKBOX: 'userInvitesCheckbox',
};

export class ViralSettingsAnalytics extends BaseInvitePeopleAnalytics {
	userInvitesCheckboxRendered(attributes: { container: string; value: boolean }) {
		this.fireUI({
			actionSubject: AnalyticsActionSubject.CHECKBOX,
			actionSubjectId: SUBJECT_IDS.USER_INVITES_CHECKBOX,
			action: AnalyticsAction.RENDERED,
			attributes: {
				...attributes,
				flow: FLOW_CORE_INVITES,
			},
		});
	}

	userInvitesCheckboxClicked(attributes: { value: boolean; container: string }) {
		this.fireUI({
			actionSubject: AnalyticsActionSubject.CHECKBOX,
			action: AnalyticsAction.CLICKED,
			// To match naming of checkbox clicked event from admin hub
			actionSubjectId: SUBJECT_IDS.USER_INVITES_CHECKBOX,
			attributes: {
				...attributes,
				flow: FLOW_CORE_INVITES,
			},
		});
	}

	directAccessCheckboxRendered(attributes: { domain: string; container: string; value: boolean }) {
		this.fireUI({
			actionSubject: AnalyticsActionSubject.CHECKBOX,
			actionSubjectId: SUBJECT_IDS.DIRECT_ACCESS_CHECKBOX,
			action: AnalyticsAction.RENDERED,
			attributes: {
				...attributes,
				flow: FLOW_CORE_INVITES,
			},
		});
	}

	directAccessCheckboxClicked(attributes: { value: boolean; domain: string; container: string }) {
		this.fireUI({
			actionSubject: AnalyticsActionSubject.CHECKBOX,
			action: AnalyticsAction.CLICKED,
			actionSubjectId: SUBJECT_IDS.DIRECT_ACCESS_CHECKBOX,
			attributes: {
				...attributes,
				flow: FLOW_CORE_INVITES,
			},
		});
	}

	createHisiFlagFailed(attributes: HisiFlagAttributes) {
		const { cloudId } = this.commonAttributes;

		this.fireOperational({
			actionSubject: AnalyticsActionSubject.USER_INVITE_SETTINGS_HISI,
			action: AnalyticsAction.CREATE_FAILED,
			attributes: { ...attributes, cloudId },
		});
	}

	createHISIFlagSucceeded(attributes: HisiFlagAttributes) {
		const { cloudId } = this.commonAttributes;

		this.fireTrack({
			actionSubject: AnalyticsActionSubject.USER_INVITE_SETTINGS_HISI,
			action: AnalyticsAction.CREATED,
			attributes: { ...attributes, cloudId },
		});
	}

	fetchHISIFlagFailed(attributes: HisiFlagAttributes) {
		const { cloudId } = this.commonAttributes;

		this.fireOperational({
			actionSubject: AnalyticsActionSubject.USER_INVITE_SETTINGS_HISI,
			action: AnalyticsAction.FETCH_FAILED,
			attributes: { ...attributes, cloudId },
		});
	}

	fetchHISIFlagSucceeded(attributes: HisiFlagAttributes) {
		const { cloudId } = this.commonAttributes;

		this.fireTrack({
			actionSubject: AnalyticsActionSubject.USER_INVITE_SETTINGS_HISI,
			action: AnalyticsAction.FETCHED,
			attributes: { ...attributes, cloudId },
		});
	}

	getUserInvitesSettingsSucceededSLO(ari: string) {
		this.fireOperational({
			actionSubject: AnalyticsActionSubject.GET_USER_INVITES_SETTINGS,
			action: AnalyticsAction.SUCCEEDED,
			attributes: { ari },
		});
	}

	getUserInvitesSettingsFailureSLO(
		attributes: {
			ari: string;
		} & FailedFetchAttributes,
	) {
		this.fireOperational({
			actionSubject: AnalyticsActionSubject.SLO_GET_USER_INVITES_SETTINGS,
			action: AnalyticsAction.FAILED,
			attributes,
		});
	}

	getDirectAccessSettingSucceededSLO(request: GetDirectAccessSettingRequest) {
		this.fireOperational({
			actionSubject: AnalyticsActionSubject.GET_DIRECT_ACCESS_SETTING,
			action: AnalyticsAction.SUCCEEDED,
			attributes: { ...request },
		});
	}

	getDirectAccessSettingFailureSLO(
		request: GetDirectAccessSettingRequest,
		attributes: {
			domain: string;
		} & FailedFetchAttributes,
	) {
		this.fireOperational({
			actionSubject: AnalyticsActionSubject.SLO_GET_DIRECT_ACCESS_SETTING,
			action: AnalyticsAction.FAILED,
			attributes: {
				...request,
				...attributes,
			},
		});
	}

	getUserInvitesSettingsFailure(
		attributes: {
			ari: string;
		} & FailedFetchAttributes,
	) {
		this.fireOperational({
			actionSubject: AnalyticsActionSubject.GET_USER_INVITES_SETTINGS,
			action: AnalyticsAction.FAILED,
			attributes,
		});
	}

	getDirectAccessSettingFailure(
		request: GetDirectAccessSettingRequest,
		attributes: {
			domain: string;
		} & FailedFetchAttributes,
	) {
		this.fireOperational({
			actionSubject: AnalyticsActionSubject.GET_DIRECT_ACCESS_SETTING,
			action: AnalyticsAction.FAILED,
			attributes: {
				...request,
				...attributes,
			},
		});
	}
}
